import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Avatar,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useSearchParams, useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { authPortal } from "../../firebase-portal";

const REQUEST_NEW_LINK_URL =
  process.env.REACT_APP_REQUEST_NEW_PORTAL_LINK_URL ||
  "https://requestnewportallink-774073159618.us-central1.run.app";

// Small shield icon for the footer trust blurb
function GoogleShield() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" style={{ display: "block" }}>
      <path d="M12 2l8 4v6c0 5.25-3.5 10-8 11-4.5-1-8-5.75-8-11V6l8-4z" fill="#4285F4" />
      <path d="M12 4l-6 3v5c0 4.2 2.8 8.1 6 9 3.2-.9 6-4.8 6-9V7l-6-3z" fill="#fff" />
      <path d="M11 7h2v5h-2zM11 13h2v2h-2z" fill="#4285F4" />
    </svg>
  );
}

export default function ClientPortalRecover() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  // If a portal user is already signed in (authPortal), skip recovery.
  useEffect(() => {
    const unsub = onAuthStateChanged(authPortal, (u) => {
      if (u) {
        navigate("/portal", { replace: true });
      }
    });
    return () => unsub();
  }, [navigate]);

  // We scope recovery attempts to a single org.
  // The expired/invalid link that dumped the user here should include ?org=ORG_ID
  const orgId = params.get("org") || "";

  // User-entered contact
  const [contact, setContact] = useState("");

  // reCAPTCHA Enterprise public site key
  const siteKey =
    process.env.REACT_APP_RECAPTCHA_SITE_KEY ||
    "6LelNPYrAAAAAOQBWoAID_sb7eERIPH9mKc3Opd9";

  // captcha state
  const [captchaReady, setCaptchaReady] = useState(false);
  const [captchaError, setCaptchaError] = useState(false);

  // ui state
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // button enabled?
  const readyToSubmit = useMemo(() => {
    return contact.trim() && orgId && !submitting;
  }, [contact, orgId, submitting]);

  // Inject the reCAPTCHA Enterprise script on mount
  useEffect(() => {
    let cancelled = false;
    const scriptId = "recaptcha-enterprise-script";
    const callbackName = "onRecaptchaEnterpriseReady";

    if (document.getElementById(scriptId) && window.grecaptcha && window.grecaptcha.enterprise) {
      setCaptchaReady(true);
      return;
    }

    window[callbackName] = () => {
      if (cancelled) return;
      if (window.grecaptcha && window.grecaptcha.enterprise && typeof window.grecaptcha.enterprise.ready === "function") {
        window.grecaptcha.enterprise.ready(() => {
          if (cancelled) return;
          setCaptchaReady(true);
        });
      } else {
        console.warn("[ClientPortalRecover] reCAPTCHA callback fired but grecaptcha.enterprise missing/invalid");
        setCaptchaError(true);
      }
    };

    const script = document.createElement("script");
    script.id = scriptId;
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}&onload=${callbackName}`;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      if (cancelled) return;
      console.warn("[ClientPortalRecover] reCAPTCHA script failed to load at all");
      setCaptchaError(true);
    };
    document.body.appendChild(script);

    return () => {
      cancelled = true;
      if (window[callbackName]) delete window[callbackName];
    };
  }, [siteKey]);

  const getCaptchaToken = useCallback(async () => {
    if (captchaError) return "dev-bypass";
    if (!window.grecaptcha || !window.grecaptcha.enterprise || typeof window.grecaptcha.enterprise.execute !== "function") {
      console.warn("[ClientPortalRecover] grecaptcha.enterprise not available at request time");
      return "dev-bypass";
    }
    try {
      const token = await window.grecaptcha.enterprise.execute(siteKey, { action: "PORTAL_RECOVER" });
      if (!token || typeof token !== "string") {
        console.warn("[ClientPortalRecover] grecaptcha.enterprise.execute returned empty token");
        return "dev-bypass";
      }
      return token;
    } catch (err) {
      console.error("[ClientPortalRecover] grecaptcha.enterprise.execute threw:", err);
      return "dev-bypass";
    }
  }, [captchaError, siteKey]);

  const handleSubmit = useCallback(async () => {
    if (!readyToSubmit) return;

    setSubmitting(true);
    setErrorMsg("");
    setSuccessMsg("");

    try {
      const captchaToken = await getCaptchaToken();
      const payload = { contact: contact.trim(), captchaToken, orgId };

      const { data } = await axios.post(REQUEST_NEW_LINK_URL, payload, { timeout: 15000 });

      if (data?.ok) {
        setSuccessMsg("If that matches our records, a new secure 2-hour access link has been sent.");
      } else {
        setErrorMsg(data?.error || "We couldn't issue a new link right now. Please contact support.");
      }
    } catch (err) {
      console.error("[ClientPortalRecover] submit failed:", err);
      const apiMsg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "We couldn't issue a new link right now. Please contact support.";
      setErrorMsg(apiMsg);
    } finally {
      setSubmitting(false);
    }
  }, [readyToSubmit, getCaptchaToken, contact, orgId]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "radial-gradient(circle at 20% 20%, #ffffff 0%, #eef2ff 60%, #e5e7eb 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Card
        elevation={3}
        sx={{
          width: "100%",
          maxWidth: 420,
          borderRadius: 2,
          border: "1px solid rgba(0,0,0,0.06)",
          boxShadow: "0 20px 40px rgba(0,0,0,0.08), 0 2px 6px rgba(0,0,0,0.04)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 2,
            borderBottom: "1px solid #E5E7EB",
            background: "linear-gradient(90deg, rgba(30,64,175,0.06) 0%, rgba(0,0,0,0) 60%)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <Avatar
              sx={{ bgcolor: "#1e40af", width: 36, height: 36, boxShadow: "0 4px 8px rgba(0,0,0,0.15)" }}
            >
              <LockOutlinedIcon fontSize="small" />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 18, lineHeight: 1.3, color: "#111827" }}>
                Request a New Secure Link
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#4B5563", lineHeight: 1.4, fontSize: 14, mt: 0.5 }}
              >
                Your previous link may have expired. We’ll send you a fresh 2-hour access link if we can verify you.
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Body */}
        <CardContent sx={{ pt: 3, pb: 2 }}>
          <Typography
            variant="body2"
            sx={{ mb: 1, color: "#374151", fontWeight: 500, fontSize: 14, lineHeight: 1.4 }}
          >
            Email or mobile number
          </Typography>

          <TextField
            placeholder="you@example.com or +1 (555) 123-4567"
            size="small"
            fullWidth
            value={contact}
            disabled={submitting}
            onChange={(e) => setContact(e.target.value)}
            inputProps={{ style: { fontSize: 14 }, "aria-label": "Your email address or mobile number" }}
            sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
          />

          <Typography
            variant="caption"
            sx={{ display: "block", mb: 2, color: "#6B7280", lineHeight: 1.4, fontSize: 12 }}
          >
            We’ll only send a link if this matches what’s already on file for you. No passwords needed.
          </Typography>

          {errorMsg && (
            <Alert severity="error" sx={{ mb: 2, fontSize: 13, lineHeight: 1.4, borderRadius: 1.5 }}>
              {errorMsg}
            </Alert>
          )}

          {successMsg && (
            <Alert severity="success" sx={{ mb: 2, fontSize: 13, lineHeight: 1.4, borderRadius: 1.5 }}>
              {successMsg}
            </Alert>
          )}

          <Button
            fullWidth
            variant="contained"
            disabled={!readyToSubmit || (!captchaReady && !captchaError)}
            onClick={handleSubmit}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: 15,
              borderRadius: 1.5,
              py: 1.2,
              backgroundColor: "#1e40af",
              ":hover": { backgroundColor: "#1e3a8a" },
              boxShadow: "0 8px 16px rgba(30,64,175,0.35), 0 2px 4px rgba(0,0,0,0.08)",
            }}
          >
            {submitting ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "center", width: "100%" }}>
                <CircularProgress size={18} sx={{ color: "white" }} />
                <span>Sending…</span>
              </Box>
            ) : (
              "Send me a new link"
            )}
          </Button>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, textAlign: "center" }}>
            <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
              <GoogleShield />
              <Typography variant="caption" sx={{ color: "#6B7280", lineHeight: 1.4, fontSize: 11.5, fontWeight: 500 }}>
                Protected by reCAPTCHA&nbsp;Enterprise
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ color: "#9CA3AF", lineHeight: 1.4, fontSize: 11 }}>
              We’ll never share your contact info. If you’re still having trouble, reply to the original email or text
              you received from us.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}